import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { toast } from '@cotera/client/app/components/ui';
import { err, ok } from 'neverthrow';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { CocoChatModel } from '../models/coco-chat.model';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

/**
 * RunToolAction for coco-chat flow.
 * Executes a tool via the resource.chat.runTool endpoint.
 * Used for approving/denying tools in both main chat and sub-agents.
 */
export class RunToolAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  static readonly actionId = 'RunToolAction';
  title = 'Run Tool';
  inputSchema = z.object({
    toolCallId: z.string(),
    conversationId: z.string(),
    action: z.enum(['approve', 'deny']),
    reason: z.string().optional(),
    skipResume: z.boolean().optional(),
  });
  group = 'Chat';

  constructor(private readonly client: Pick<TenantedClient, 'resource'>) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: {
      toolCallId: string;
      conversationId: string;
      action: 'approve' | 'deny';
      reason?: string;
      skipResume?: boolean;
    },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel)!;

    model.statusWatchable.set('submitted');

    const result = await this.client.resource.chat.runTool({
      model: model.model,
      conversationId: params.conversationId,
      toolCallId: params.toolCallId,
      action: params.action,
      reason: params.reason,
      ...(params.skipResume === true && { skipResume: true }),
    });

    if (result.isErr()) {
      model.statusWatchable.set('error');
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to execute tool';
      toast.error(errorMessage);
      return err([{ t: 'error', message: errorMessage }]);
    }

    void model.chatStatusWatchable.refresh();

    // Fallback: if status stays 'submitted', force to ready
    setTimeout(() => {
      if (model.statusWatchable.snapshot() === 'submitted') {
        model.statusWatchable.set('ready');
      }
    }, 2000);

    return ok({});
  }

  track({
    payload,
    context,
  }: ActionTrackingContext<
    {
      toolCallId: string;
      conversationId: string;
      action: 'approve' | 'deny';
      reason?: string;
    },
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.ToolExecuted, {
      conversationId: payload.conversationId,
      surface: getChatSurface(context),
      toolCallId: payload.toolCallId,
      action: payload.action,
      hasReason: (payload.reason ?? '').trim().length > 0,
    });
  }
}
